import {
  formatDate,
  formatAmount,
  formatString,
  formatDateTime,
  getFileNameFromUrl,
} from "../../../components/Helper";
import { pdf } from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import Spinner from "../../../theme/Spinner";
import { CURRENCY } from "../../../components/Constants";
import { getSettingsApi } from "../../../services/_settings";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getSalesInvoiceDetailsByIdApi,
  saveSalesInvoiceRemarkApi,
  updateSalesInvoiceMyDocumentStatus,
} from "../../../services/_sales";
import { SelfDocuments } from "../../../components/Enums";
import PhytoSanitaryCertificate from "./invoices/PhytoSanitaryCertificate";
import AnnexureInvoice from "./invoices/AnnexureInvoice";
import CommercialInvoice from "./invoices/CommercialInvoice";
import ExportInvoiceCumPackingList from "./invoices/ExportInvoiceCumPackingList";
import ProformaInvoice from "./invoices/ProformaInvoice";
import BlDraftInvoice from "./invoices/BlDraftInvoice";
import HealthAndQualityCertificate from "./invoices/HealthAndQualityCertificate";
import CertificateOfNonGmo from "./invoices/CertificateOfNonGmo";
import WeightAndQualityCertificate from "./invoices/WeightAndQualityCertificate";
import CertificateOfOrigin from "./invoices/CertificateOfOrigin";
import FormAiDraft from "./invoices/FormAiDraft";
import VgmCertificate from "./invoices/VGM_Certificate";
import JSZip from "jszip";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { errorToast, successToast } from "../../../components/Toast";
import { getLoginUserType, getPermissions } from "../../../context/AuthContext";
import PriorNoticeInvoice from "./invoices/PriorNoticeInvoice";
import FumigationCertificateInvoice from "./invoices/FumigationCertificateInvoice";
import PackingListInvoice from "./invoices/PackingListINvoice";
import { getCompanyDetailsApi } from "../../../services/_company";

const ViewSalesInvoice: React.FC = () => {
  const { id } = useParams();
  const { isBroker, isBuyer } = getLoginUserType();
  const permissions = getPermissions();
  const navigate = useNavigate();
  const [salesInvoiceData, setSalesInvoiceData] = useState<any>({});
  const { isAdmin } = getLoginUserType();
  const [loading, setLoading] = useState<boolean>(false);
  const [settingData, setSettingData] = useState<any>({});
  const [companyData, setCompanyData] = useState<any>({});
  const [isReadyDownload, setIsReadyDownload] = useState(false);
  const [downloadAllLoading, setDownloadAllLoading] = useState<string[]>([]);
  const [saveBtnEnabledIds, setSaveBtnEnabledIds] = useState<string[]>([]);
  const [remarkInput, setRemarkInput] = useState<{ [key: string]: string }>({});
  const [isSaveDisabled, setIsSaveDisabled] = useState<{
    [key: string]: boolean;
  }>({ false: false });
  const [signatureState, setSignatureState] = useState<{
    [key: string]: boolean;
  }>({});
  const [downloadLoadingIds, setDownloadLoadingIds] = useState<string[]>([]);
  const [remarkLoadingIds, setRemarkLoadingIds] = useState<string[]>([]);
  const [previewLoadingIds, setPreviewLoadingIds] = useState<string[]>([]);

  const [openSections, setOpenSections] = useState<{
    [productId: string]: {
      shipmentDetails?: boolean;
      container?: boolean;
      myDocument?: boolean;
      otherDocument?: boolean;
      bill?: boolean;
    };
  }>({});

  const SIGNATURE_SUPPORTED_DOCS: SelfDocuments[] = [
    SelfDocuments.PACKING_LIST,
    SelfDocuments.EXPORT_INVOICE_CUM_PACKING_LIST,
    SelfDocuments.ANNEXURE_C,
    SelfDocuments.COMMERCIAL_INVOICE,
    SelfDocuments.VERIFIED_GROSS_MASS,
    SelfDocuments.PRIOR_NOTICE,
  ];

  useEffect(() => {
    if (!id) {
      navigate(`sales-invoice-list`);
    }
  });

  const fetchInitialData = async () => {
    setLoading(true);
    const result: any = await getSalesInvoiceDetailsByIdApi(id as string);
    if (result.isSuccess) {
      const documentOrder = Object.values(SelfDocuments);

      result.data.my_documents.sort((a: any, b: any) => {
        const indexA = documentOrder.indexOf(a.document.slug);
        const indexB = documentOrder.indexOf(b.document.slug);
        return (
          (indexA === -1 ? documentOrder.length : indexA) -
          (indexB === -1 ? documentOrder.length : indexB)
        );
      });

      setSalesInvoiceData(result.data);

      if (result.data?.my_documents?.length > 0) {
        setOpenSections((prev) => ({
          ...prev,
          [result.data.id]: {
            ...prev[result.data.id],
            myDocument: true,
          },
        }));
      }

      const remarkMap =
        result.data?.my_documents?.reduce(
          (acc: { [key: string]: string }, doc: any) => {
            acc[`${result.data.id}_${doc.document_id}`] = doc.remark || "";
            return acc;
          },
          {},
        ) || {};

      setRemarkInput(remarkMap);

      const initialSaveDisabledState = Object.keys(remarkMap).reduce(
        (acc: { [key: string]: boolean }, docId) => {
          acc[docId] = true;
          return acc;
        },
        {},
      );
      setIsSaveDisabled(initialSaveDisabledState);

      setTimeout(async () => {
        const shipperDetails: any = await getSettingsApi();
        if (shipperDetails.isSuccess) {
          const data = Object.entries(shipperDetails.data).reduce(
            (acc: any, setting: any) => {
              acc[setting[0]] = setting[1];
              return acc;
            },
            {},
          );

          const updatedSettingData = {
            ...data,
            invoice_digital_signature: `${process.env.REACT_APP_BACKEND_URL}${data.invoice_digital_signature}`,
          };
          setSettingData(updatedSettingData);
        }
        setIsReadyDownload(true);
      }, 500);
    }

    const companyDetailsResult: any = await getCompanyDetailsApi();
    if (companyDetailsResult.isSuccess) {
      setCompanyData(companyDetailsResult?.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const downloadAllDocuments = async (salesInvoiceProductsData: any) => {
    setDownloadAllLoading((prev) =>
      prev.includes(salesInvoiceProductsData?.product_id)
        ? prev
        : [...prev, salesInvoiceProductsData?.product_id],
    );

    try {
      const zip = new JSZip();
      const zipFileName = `Documents-${salesInvoiceData?.invoice_no}.zip`;
      const documents = salesInvoiceProductsData?.my_documents || [];
      if (documents.length > 0) {
        for (const d of salesInvoiceProductsData?.my_documents) {
          const documentSlug = d?.document?.slug;
          const includeSignature =
            signatureState[`${salesInvoiceProductsData.id}_${d.document_id}`] ??
            true;
          const invoiceComponent = await getInvoiceComponent(
            salesInvoiceProductsData,
            documentSlug,
            includeSignature,
          );
          if (invoiceComponent) {
            zip.file(`${documentSlug}.pdf`, invoiceComponent);
          } else {
            console.warn(
              "Document Slug not recognized, skipping PDF generation for:",
              documentSlug,
            );
          }
        }
        const zipGenerated = await zip.generateAsync({ type: "blob" });
        saveAs(zipGenerated, zipFileName);
      } else {
        toast.error("No documents to download");
      }
      setDownloadAllLoading((prev) =>
        prev.filter((id) => id !== salesInvoiceProductsData?.product_id),
      );
    } catch (error) {
      console.error("Error during download:", error);
    }
  };

  const handleChangeMyDocumentStatus = async (
    sales_invoice_id: string,
    sales_invoice_product_id: string,
    document_id: string,
    is_checked: boolean,
  ) => {
    setLoading(true);
    const result: any = await updateSalesInvoiceMyDocumentStatus({
      sales_invoice_id,
      sales_invoice_product_id,
      document_id,
      is_checked,
    });
    if (result.isSuccess) {
      successToast(result.message);
      // const updatedProducts = salesInvoiceData?.sales_invoice_products?.map(
      //   (product: any) => {
      //     if (product.id === sales_invoice_product_id) {
      //       const updatedDocuments = product.sales_invoice_my_documents.map(
      //         (doc: any) =>
      //           doc.document_id === document_id ? { ...doc, is_checked } : doc
      //       );
      //       return { ...product, sales_invoice_my_documents: updatedDocuments };
      //     }
      //     return product;
      //   }
      // );

      fetchInitialData();
    } else {
      errorToast(result.message);
    }
    setLoading(false);
  };

  const toggleSection = (InvoiceId: string, section: string) => {
    setOpenSections((prev: any) => ({
      ...prev,
      [InvoiceId]: {
        ...prev[InvoiceId],
        [section]: !prev[InvoiceId]?.[section],
      },
    }));
  };

  const handleSaveRemark = async (
    my_doc_id: string,
    sales_invoice_product_id: string,
  ) => {
    const key = `${sales_invoice_product_id}_${my_doc_id}`;

    setRemarkLoadingIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
    try {
      const remarkTosend = remarkInput[key] || "";
      const remarkResult = await saveSalesInvoiceRemarkApi({
        sales_invoice_id: salesInvoiceData?.id,
        sales_invoice_product_id,
        document_id: my_doc_id,
        remark: remarkTosend,
      });
      if (remarkResult.isSuccess) {
        setRemarkInput((prev) => ({ ...prev, [key]: remarkTosend }));
        setRemarkLoadingIds((prev) => prev.filter((id) => id !== key));
        setSaveBtnEnabledIds((prev) => prev.filter((id) => id !== key));
        toast.success(remarkResult.message);
      } else {
        toast.error(remarkResult.message);
      }
    } catch (error) {
      console.error("Error during save remark:", error);
    }
  };

  const previewInvoice = async (
    salesInvoiceProductsData: any,
    slug: SelfDocuments,
    my_doc_id: string,
  ) => {
    try {
      setPreviewLoadingIds((prev) =>
        prev.includes(my_doc_id) ? prev : [...prev, my_doc_id],
      );
      const includeSignature =
        signatureState[`${salesInvoiceProductsData.id}_${my_doc_id}`] ?? true;
      const invoiceComponent = await getInvoiceComponent(
        salesInvoiceProductsData,
        slug,
        includeSignature,
      );
      if (invoiceComponent) {
        const pdfUrl = URL.createObjectURL(invoiceComponent);
        setPreviewLoadingIds((prev) => prev.filter((id) => id !== my_doc_id));
        window.open(pdfUrl, "_blank");
      }
      setPreviewLoadingIds((prev) => prev.filter((id) => id !== my_doc_id));
    } catch (err) {
      console.error(`Error in previewing Invoice:`, err);
    }
  };

  const downloadInvoice = async (
    salesInvoiceProductsData: any,
    slug: SelfDocuments,
    my_doc_id: string,
  ) => {
    try {
      if (isReadyDownload) {
        setDownloadLoadingIds((prev) =>
          prev.includes(my_doc_id) ? prev : [...prev, my_doc_id],
        );
        const includeSignature =
          signatureState[`${salesInvoiceProductsData.id}_${my_doc_id}`] ?? true;
        const invoiceComponent = await getInvoiceComponent(
          salesInvoiceProductsData,
          slug,
          includeSignature,
        );
        if (invoiceComponent) {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(invoiceComponent);
          link.download = `${salesInvoiceData?.invoice_no}-${slug.replace(
            "_",
            "-",
          )}.pdf`;
          link.click();
        }
        setDownloadLoadingIds((prev) => prev.filter((id) => id !== my_doc_id));
      }
    } catch (err) {
      console.error(`Error in previewing Invoice:`, err);
    }
  };

  const getInvoiceComponent = async (
    salesInvoiceProductsData: any,
    slug: SelfDocuments,
    includeSignature: boolean = true,
  ) => {
    switch (slug) {
      case SelfDocuments.PHYTOSANITARY_CERTIFICATE:
        return await pdf(
          <PhytoSanitaryCertificate
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
          />,
        ).toBlob();
      case SelfDocuments.ANNEXURE_C:
        return await pdf(
          <AnnexureInvoice
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
            includeSignature={includeSignature}
          />,
        ).toBlob();
      case SelfDocuments.PACKING_LIST:
        return await pdf(
          <PackingListInvoice
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
            includeSignature={includeSignature}
          />,
        ).toBlob();
      case SelfDocuments.COMMERCIAL_INVOICE:
        return await pdf(
          <CommercialInvoice
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
            includeSignature={includeSignature}
          />,
        ).toBlob();
      case SelfDocuments.EXPORT_INVOICE_CUM_PACKING_LIST:
        return await pdf(
          <ExportInvoiceCumPackingList
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
            includeSignature={includeSignature}
          />,
        ).toBlob();
      case SelfDocuments.PROFORMA_INVOICE:
        return await pdf(
          <ProformaInvoice
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
          />,
        ).toBlob();
      case SelfDocuments.BILL_OF_LADING:
        return await pdf(
          <BlDraftInvoice
            settingData={settingData}
            salesInvoiceData={salesInvoiceData}
          />,
        ).toBlob();
      case SelfDocuments.HEALTH_AND_QUALITY_CERTIFICATE:
        return await pdf(
          <HealthAndQualityCertificate
            settingsData={settingData}
            salesInvoiceData={salesInvoiceData}
            companyData={companyData}
          />,
        ).toBlob();
      case SelfDocuments.CERTIFICATE_OF_NON_GMO:
        return await pdf(
          <CertificateOfNonGmo
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
          />,
        ).toBlob();
      case SelfDocuments.WEIGHT_AND_QUALITY_CERTIFICATE:
        return await pdf(
          <WeightAndQualityCertificate
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
          />,
        ).toBlob();
      case SelfDocuments.CERTIFICATE_OF_ORIGIN:
        return await pdf(
          <CertificateOfOrigin
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
          />,
        ).toBlob();
      case SelfDocuments.FORM_AI:
        return await pdf(
          <FormAiDraft
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
          />,
        ).toBlob();
      case SelfDocuments.VERIFIED_GROSS_MASS:
        return await pdf(
          <VgmCertificate
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
            companyData={companyData}
            includeSignature={includeSignature}
          />,
        ).toBlob();
      case SelfDocuments.PRIOR_NOTICE:
        return await pdf(
          <PriorNoticeInvoice
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
            includeSignature={includeSignature}
          />,
        ).toBlob();
      case SelfDocuments.FUMIGATION_CERTIFICATE:
        return await pdf(
          <FumigationCertificateInvoice
            salesInvoiceData={salesInvoiceData}
            settingsData={settingData}
          />,
        ).toBlob();
      default:
        return;
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header pb-0 d-flex justify-content-between">
          <div>
            <h5>Commercial Invoice Details :</h5>
          </div>
          <div className="d-flex" style={{ gap: "10px" }}>
            <Link
              to={`/sales-invoice-list`}
              type="button"
              className="btn btn-label-github"
            >
              Back
            </Link>
          </div>
        </div>
        <div className="card-body">
          {loading && <Spinner isFullScreen={false} />}
          <div>
            <div
              className="col-12 col-sm-6 col-md-4 col-lg-3 row m-1 p-2 d-flex info-container text-capitalize"
              style={{
                border: "1px solid #d1d0d4",
                borderRadius: "0.375rem",
                width: "100%",
              }}
            >
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Invoice No :</span>
                {salesInvoiceData?.invoice_no || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Contract No :</span>
                {Array.from(
                  new Set(
                    salesInvoiceData?.sales_invoice_products?.map(
                      (p: any) => p?.sales_contract?.contract_no,
                    ),
                  ),
                ).join(", ") || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Buyer :</span>
                {salesInvoiceData?.buyer?.name || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Invoice Date:</span>
                {formatDate(salesInvoiceData.invoice_date)}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Status:</span>
                {formatString(salesInvoiceData.status)}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Logistic :</span>
                {salesInvoiceData?.logistic?.name || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">CHA :</span>
                {salesInvoiceData?.cha?.name || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Notify Party :</span>
                {salesInvoiceData?.notify_party?.name || "-"}
              </div>

              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total Quantity :</span>
                {salesInvoiceData?.total_quantity
                  ? `${formatAmount(salesInvoiceData?.total_quantity, 3)} MTS`
                  : "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total Amount :</span>
                {CURRENCY.USD.SYMBOL}
                {formatAmount(salesInvoiceData.total_amount) || `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Exchange Rate :</span>
                {CURRENCY.INR.SYMBOL}
                {formatAmount(salesInvoiceData.exchange_rate) || `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total Taxable Amount :</span>
                {CURRENCY.INR.SYMBOL}
                {formatAmount(salesInvoiceData.total_taxable_amount_inr) || `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">IGST Percentage:</span>
                {salesInvoiceData?.igst_percentage
                  ? `${salesInvoiceData?.igst_percentage}%`
                  : "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total IGST Amount :</span>
                {CURRENCY.INR.SYMBOL}
                {formatAmount(salesInvoiceData.total_igst_amount_inr) || `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Commission Rate:</span>
                {salesInvoiceData?.commission_rate
                  ? `${salesInvoiceData?.commission_rate}%`
                  : "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Commission Amount :</span>
                {CURRENCY.USD.SYMBOL}
                {formatAmount(salesInvoiceData.commission_amount) || `-`}
              </div>

              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total No of Bags :</span>
                {salesInvoiceData?.total_no_of_bags || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total Packing Net Weight :</span>
                {salesInvoiceData?.total_net_weight
                  ? `${formatAmount(salesInvoiceData?.total_net_weight)} KGS`
                  : `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Total Packing Gross Weight :</span>
                {salesInvoiceData?.total_gross_weight
                  ? `${formatAmount(salesInvoiceData?.total_gross_weight)} KGS`
                  : `-`}
              </div>

              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Origin Port :</span>
                {salesInvoiceData?.origin_port?.name || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">ETD:</span>
                {formatDateTime(salesInvoiceData.etd) || `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Destination Port :</span>
                {salesInvoiceData?.destination_port || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">ETA:</span>
                {formatDateTime(salesInvoiceData.eta) || `-`}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Pre Carriage By :</span>
                {salesInvoiceData?.pre_carriage_by || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Vessel No :</span>
                {salesInvoiceData?.vessel_no || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Delivery Term :</span>
                {salesInvoiceData?.delivery_term || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">Payment Term :</span>
                {salesInvoiceData?.payment_term || "-"}
              </div>
              <div className="col-12 col-lg-4 col-md-4 mb-3">
                <span className="h6 me-1">BL No :</span>
                {salesInvoiceData?.bl_no || "-"}
              </div>
            </div>
          </div>

          <div>
            <div className="mt-7 py-2 pb-0 d-flex justify-content-start align-items-center">
              <h5>Product Details :</h5>
            </div>
            <div className="card-datatable table-responsive">
              <table className="dt-advanced-search table table-nowrap">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product Name</th>
                    <th>Price</th>
                    <th>Quantity (MTS)</th>
                    <th>No Of Bags</th>
                    <th>Package Type</th>
                    <th>Packing Net Weight (KGS)</th>
                    <th>Total Packing Net Weight (KGS)</th>
                    <th>Packing Gross Weight (KGS)</th>
                    <th>Total Packing Gross Weight (KGS)</th>
                    {/* <th>Packing Unit</th> */}
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {salesInvoiceData?.sales_invoice_products &&
                  salesInvoiceData?.sales_invoice_products.length > 0 ? (
                    salesInvoiceData?.sales_invoice_products.map(
                      (row: any, index: number) => {
                        return (
                          <tr key={row.id}>
                            <td>{index + 1}</td>
                            <td>{row?.product?.name || "Product"}</td>
                            <td>
                              {CURRENCY.USD.SYMBOL}
                              {formatAmount(row?.price)}
                            </td>
                            <td>
                              {formatAmount(row?.quantity, 3)}
                              {/* {stringToUpperCase(row?.quantity_unit?.name)} */}
                            </td>
                            <td>{row?.no_of_bags || "-"}</td>
                            <td>{row?.packing_type?.name || "-"}</td>
                            <td>
                              {row?.packing_net_weight
                                ? formatAmount(row?.packing_net_weight, 3)
                                : "-"}
                            </td>
                            <td>
                              {row?.total_net_weight
                                ? formatAmount(row.total_net_weight, 3)
                                : "-"}
                            </td>
                            <td>
                              {row?.packing_gross_weight
                                ? formatAmount(row?.packing_gross_weight, 3)
                                : "-"}
                            </td>
                            <td>
                              {row?.total_gross_weight
                                ? formatAmount(row.total_gross_weight, 3)
                                : "-"}
                            </td>
                            {/* <td>{row?.packing_unit?.name || ""}</td> */}
                            <td>
                              {CURRENCY.USD.SYMBOL}
                              {formatAmount(row?.total_amount)}
                            </td>
                          </tr>
                        );
                      },
                    )
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No products available
                      </td>
                    </tr>
                  )}

                  {/* Subtotal row */}
                  <tr>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th>{formatAmount(salesInvoiceData.total_quantity, 3)}</th>
                    <th>{salesInvoiceData.total_no_of_bags}</th>
                    <th></th>
                    <th></th>
                    <th>
                      {formatAmount(salesInvoiceData.total_net_weight, 3)}
                    </th>
                    <th></th>
                    <th>
                      {formatAmount(salesInvoiceData.total_gross_weight, 3)}
                    </th>
                    {/* <th></th> */}
                    <th>
                      {CURRENCY.USD.SYMBOL}
                      {formatAmount(salesInvoiceData.total_amount)}
                    </th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {salesInvoiceData?.containers?.length > 0 && (
            <>
              <div className="mt-7 py-2 pb-0 d-flex justify-content-start align-items-center">
                <h5>Container Details :</h5>
              </div>
              <div className="card-datatable table-responsive">
                <table className="dt-advanced-search table table-nowrap">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>Container No</th>
                      <th>Lot No</th>
                      <th>Lot Label Seal</th>
                      <th>Line Seal No</th>
                      <th>Self Seal No</th>
                      <th>Sticker No</th>
                      <th>Vehicle No</th>
                      <th>Size</th>
                      <th>No of Bags</th>
                      <th>Total Net Weight</th>
                      <th>Total Gross Weight</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesInvoiceData?.containers?.map(
                      (row: any, index: number) => {
                        return (
                          <tr key={row.id}>
                            <td>{index + 1}</td>
                            <td>{row?.product.name}</td>
                            <td>{row?.container_no || "-"}</td>
                            <td>{row?.lot_no || "-"}</td>
                            <td>{row?.lot_label_seal || "-"}</td>
                            <td>{row?.line_seal_no || "-"}</td>
                            <td>{row?.self_seal?.seal_no || "-"}</td>
                            <td>{row?.sticker_no || "-"}</td>
                            <td>{row?.vehicle_no || "-"}</td>
                            <td>{row?.size || "-"}</td>
                            <td>
                              {row?.no_of_bags || "-"}{" "}
                              {row?.packing_type?.name || ""}
                            </td>
                            <td>
                              {formatAmount(row?.total_net_weight) || "-"} KG
                            </td>
                            <td>
                              {formatAmount(row?.total_gross_weight) || "-"} KG
                            </td>
                            <td>{row?.remark || "-"}</td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {salesInvoiceData?.my_documents?.length > 0 && (
            <>
              <div className="card my-card mt-4">
                <div className="card-header my-card-header bg-lighter d-flex justify-content-between py-2">
                  <div className="px-0 row d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="my-card-header-head">
                        My Documents (Self)
                      </h6>
                    </div>
                  </div>

                  <div className="col-6 col-sm-2 d-flex justify-content-end">
                    <button
                      className="btn btn-primary btn-sm"
                      type="button"
                      title="Download All Documents"
                      onClick={() => {
                        downloadAllDocuments(salesInvoiceData);
                      }}
                      disabled={downloadAllLoading.includes(
                        salesInvoiceData?.product_id,
                      )}
                    >
                      {downloadAllLoading.includes(
                        salesInvoiceData?.product_id,
                      ) && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                      Download All
                    </button>
                    <button
                      title="Add Containers"
                      className="btn btn-sm btn-primary waves-effect waves-light p-0"
                      type="button"
                      style={{ margin: "5px" }}
                      onClick={() =>
                        toggleSection(salesInvoiceData.id, "myDocument")
                      }
                    >
                      <span>
                        {openSections[salesInvoiceData.id]?.myDocument ? (
                          <span>
                            <i className="ti ti-chevron-up my-card-icon-button"></i>
                          </span>
                        ) : (
                          <span>
                            <i className="ti ti-chevron-down my-card-icon-button"></i>
                          </span>
                        )}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Collapse content */}
                {openSections[salesInvoiceData.id]?.myDocument && (
                  <div className="card-body my-card-body">
                    <div className="card-datatable table-responsive collapse show ">
                      <table className="dt-advanced-search table table-nowrap">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Document</th>
                            {permissions.includes("update-sales-invoice") && (
                              <th className="text-center">Is Verified</th>
                            )}
                            {(permissions.includes("update-sales-invoice") ||
                              isBroker ||
                              isBuyer) && <th>Remark</th>}
                            <th className="text-center">Signature</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesInvoiceData?.my_documents?.map(
                            (d: any, i: number) => (
                              <tr key={d.document_id}>
                                <td>{i + 1}</td>
                                <td>{d?.document?.name}</td>

                                {/* Is Verified */}
                                {permissions.includes(
                                  "update-sales-invoice",
                                ) && (
                                  <td>
                                    <div className="form-check form-switch d-flex justify-content-center">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`status-${d.document_id}`}
                                        defaultChecked={d.is_checked}
                                        onClick={() => {
                                          handleChangeMyDocumentStatus(
                                            salesInvoiceData.id,
                                            salesInvoiceData.id,
                                            d.document_id,
                                            !d.is_checked,
                                          );
                                        }}
                                      />
                                    </div>
                                  </td>
                                )}

                                {/* Remark */}
                                {(permissions.includes(
                                  "update-sales-invoice",
                                ) ||
                                  isBroker ||
                                  isBuyer) && (
                                  <td>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <textarea
                                        id={`remark-${d.document_id}`}
                                        name="remark"
                                        className="form-control"
                                        value={
                                          remarkInput[
                                            `${salesInvoiceData.id}_${d.document_id}`
                                          ] || ""
                                        }
                                        onChange={(e) => {
                                          const newValue = e.target.value;

                                          if (
                                            newValue !==
                                              remarkInput[
                                                `${salesInvoiceData.id}_${d.document_id}`
                                              ] ||
                                            ""
                                          ) {
                                            setSaveBtnEnabledIds((prev) =>
                                              prev.includes(
                                                `${salesInvoiceData.id}_${d.document_id}`,
                                              )
                                                ? prev
                                                : [
                                                    ...prev,
                                                    `${salesInvoiceData.id}_${d.document_id}`,
                                                  ],
                                            );
                                          } else {
                                            setSaveBtnEnabledIds((prev) =>
                                              prev.filter(
                                                (id) =>
                                                  id !==
                                                  `${salesInvoiceData.id}_${d.document_id}`,
                                              ),
                                            );
                                          }

                                          setRemarkInput((prev) => ({
                                            ...prev,
                                            [`${salesInvoiceData.id}_${d.document_id}`]:
                                              newValue || "",
                                          }));
                                        }}
                                        rows={2}
                                      />
                                      <button
                                        className="btn btn-primary btn-sm m-2"
                                        type="button"
                                        title={`Save Remark of ${d.document?.name}`}
                                        onClick={() =>
                                          handleSaveRemark(
                                            d.document_id,
                                            salesInvoiceData.id,
                                          )
                                        }
                                        disabled={
                                          !saveBtnEnabledIds.includes(
                                            `${salesInvoiceData.id}_${d.document_id}`,
                                          )
                                        }
                                      >
                                        {remarkLoadingIds.includes(
                                          `${salesInvoiceData.id}_${d.document_id}`,
                                        )
                                          ? "Saving..."
                                          : "Save"}
                                      </button>
                                    </div>
                                  </td>
                                )}

                                {/* digtal signature check box */}
                                <td className="text-center">
                                  {SIGNATURE_SUPPORTED_DOCS.includes(
                                    d?.document?.slug,
                                  ) && (
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={
                                        signatureState[
                                          `${salesInvoiceData.id}_${d.document_id}`
                                        ] ?? true
                                      }
                                      onChange={(e) => {
                                        setSignatureState((prev) => ({
                                          ...prev,
                                          [`${salesInvoiceData.id}_${d.document_id}`]:
                                            e.target.checked,
                                        }));
                                      }}
                                      title="Include digital signature"
                                    />
                                  )}
                                </td>

                                {/* Action Buttons */}
                                <td className="text-center">
                                  <button
                                    className="btn btn-primary btn-sm me-2"
                                    type="button"
                                    onClick={() =>
                                      previewInvoice(
                                        salesInvoiceData,
                                        d?.document?.slug,
                                        d.document_id,
                                      )
                                    }
                                  >
                                    Preview
                                  </button>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    type="button"
                                    onClick={() =>
                                      downloadInvoice(
                                        salesInvoiceData,
                                        d?.document?.slug,
                                        d.document_id,
                                      )
                                    }
                                  >
                                    Download
                                  </button>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {salesInvoiceData?.other_documents?.length > 0 && (
            <>
              <div className="mt-7 py-2 pb-0 d-flex justify-content-start align-items-center">
                <h5>Other Document Details :</h5>
              </div>
              <div className="card-datatable table-responsive">
                <table className="dt-advanced-search table table-nowrap">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Document</th>
                      <th>Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesInvoiceData?.other_documents?.map(
                      (row: any, index: number) => {
                        return (
                          <tr key={row.document_id}>
                            <td>{index + 1}</td>
                            <td> {row?.document?.name || "N/A"}</td>
                            <td className="align-items-center justify-content-left d-flex">
                              <div
                                className="d-flex"
                                style={{
                                  height: "100%",
                                  alignItems: "center",
                                }}
                              >
                                {row?.files?.map((e: any) => {
                                  const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${e}`;
                                  return (
                                    <div className="row p-0">
                                      <div
                                        className="col-md mb-md-0"
                                        style={{
                                          width: "170px",
                                          height: "100%",
                                          textWrap: "wrap",
                                        }}
                                      >
                                        <div className="form-check custom-option custom-option-icon">
                                          <label
                                            className="form-check-label custom-option-content"
                                            onClick={() =>
                                              window.open(imageUrl, "_blank")
                                            }
                                          >
                                            <span className="custom-option-body">
                                              <small>
                                                {getFileNameFromUrl(imageUrl)}
                                              </small>
                                            </span>
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {salesInvoiceData?.bills?.length > 0 && (
            <>
              <div className="mt-7 py-2 pb-0 d-flex justify-content-start align-items-center">
                <h5>Bill Details :</h5>
              </div>
              <div className="card-datatable table-responsive">
                <table className="dt-advanced-search table table-nowrap">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Bill Type</th>
                      <th>Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesInvoiceData?.bills?.map((row: any, index: number) => {
                      return (
                        <tr key={row.bill_id}>
                          <td>{index + 1}</td>
                          <td> {row?.bill?.name || "N/A"}</td>
                          <td className="align-items-center justify-content-left d-flex">
                            <div
                              className="d-flex"
                              style={{
                                height: "100%",
                                alignItems: "center",
                              }}
                            >
                              {row?.files?.map((e: any) => {
                                const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${e}`;
                                return (
                                  <div className="row p-0">
                                    <div
                                      className="col-md mb-md-0"
                                      style={{
                                        width: "170px",
                                        height: "100%",
                                        textWrap: "wrap",
                                      }}
                                    >
                                      <div className="form-check custom-option custom-option-icon">
                                        <label
                                          className="form-check-label custom-option-content"
                                          onClick={() =>
                                            window.open(imageUrl, "_blank")
                                          }
                                        >
                                          <span className="custom-option-body">
                                            <small>
                                              {getFileNameFromUrl(imageUrl)}
                                            </small>
                                          </span>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {salesInvoiceData?.sales_invoice_supplier_products?.length > 0 &&
            isAdmin && (
              <>
                <div className="card my-card mt-4">
                  <div className="card-header my-card-header bg-lighter d-flex justify-content-between">
                    <div>
                      <h6 className="my-card-header-head">
                        Supplier Products Details
                      </h6>
                    </div>
                  </div>
                  <div className="card-body my-card-body">
                    <div className="card-datatable table-responsive ">
                      <table className="dt-advanced-search table table-nowrap">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Product</th>
                            <th>Purchase Invoice</th>
                            <th>Supplier</th>
                            <th>Quantity (MTS)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesInvoiceData
                            ? salesInvoiceData?.sales_invoice_supplier_products?.map(
                                (row: any, index: number) => {
                                  return (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>{row?.product_name || "N/A"}</td>
                                      <td>{row?.purchase_invoice_no}</td>
                                      <td>{row?.supplier_name}</td>
                                      <td>{formatAmount(row?.quantity, 3)}</td>
                                    </tr>
                                  );
                                },
                              )
                            : ""}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
        </div>
      </div>
    </>
  );
};

export default ViewSalesInvoice;
